import { Table } from 'rsuite';
import React, { useState } from 'react';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import { trpc, RouterInput } from '../../../../api/trpc';
import { ModalsService } from '../../../../services/modals.service';
import { CustomsDeliveryDutyEditModal } from './customs-duty-edit-modal';
import { CellProps } from 'rsuite/Table';
import {
  CleanCustomsDeliveryDuty,
  RbacObject,
} from '../../../../export-types/cleaned-types';
import { getCurrencySymbol, toMoney } from '../../../../utils/common.utils';
import { Rbac, useRbac } from '../../../common/Rbac';
import IconButton from 'rsuite/IconButton';
import { WhisperSpeaker } from '../../../../utils/whisper-speaker';
import Popover from 'rsuite/Popover';
import Dropdown from 'rsuite/Dropdown';
import Whisper from 'rsuite/Whisper';
import { ConfirmModal } from '../../../common/confirm-modal';
import { NavLink } from 'react-router-dom';

type FormType = 'code' | 'price';

const ConditionCell = ({
  rowData,
  dataKey,
  ...props
}: CellProps<CleanCustomsDeliveryDuty>) => {
  let text = '';

  if (rowData?.fromPrice) {
    text = `от ${toMoney(rowData?.fromPrice)} ${getCurrencySymbol('rub')} `;
  }
  if (rowData?.toPrice) {
    text += `до ${toMoney(rowData?.toPrice)} ${getCurrencySymbol('rub')}`;
  }

  return rowData && <Table.Cell {...props}>{text}</Table.Cell>;
};

const RateCell = ({
  rowData,
  dataKey,
  ...props
}: CellProps<CleanCustomsDeliveryDuty>) =>
  rowData && (
    <Table.Cell {...props}>
      {toMoney(rowData.rate)} {getCurrencySymbol('rub')}
    </Table.Cell>
  );

const CodeCell = ({
  rowData,
  dataKey,
  ...props
}: CellProps<CleanCustomsDeliveryDuty>) =>
  rowData && <Table.Cell {...props}>{rowData.goodCode}</Table.Cell>;

export const CustomsDeliveryDutiesTab = () => {
  const { hasPermission } = useRbac();
  const [type, setType] = useState<FormType>('price');

  const input = { type: type };
  const { data, isLoading, mutate } = useSWR(
    ['customsDeliveryDuty.getList', input],
    () => trpc.customsDeliveryDuty.getList.query(input),
  );
  const { trigger: updateDuty } = useSWRMutation(
    ['customsDeliveryDuty.updateCustomsDuty'],
    (
      _k,
      { arg }: { arg: RouterInput['customsDeliveryDuty']['updateCustomsDuty'] },
    ) => trpc.customsDeliveryDuty.updateCustomsDuty.mutate(arg),
  );
  const { trigger: deleteDuty } = useSWRMutation(
    ['customsDeliveryDuty.deleteCustomsDuty'],
    (
      _k,
      { arg }: { arg: RouterInput['customsDeliveryDuty']['deleteCustomsDuty'] },
    ) => trpc.customsDeliveryDuty.deleteCustomsDuty.mutate(arg),
  );
  const canEdit = hasPermission(RbacObject.CustomsDelivery, 'write:settings');

  const renderMenu =
    (duty: CleanCustomsDeliveryDuty): WhisperSpeaker =>
    ({ onClose, left, top, className }, ref) => {
      const handleSelect = async (eventKey: string | undefined) => {
        onClose();
        if (eventKey === '1') {
          ModalsService.createModal(CustomsDeliveryDutyEditModal, {
            defaultValues: {
              ...duty,
              fromPrice: duty.fromPrice || undefined,
              toPrice: duty.toPrice || undefined,
              goodCode: duty.goodCode || undefined,
            },
            title: 'Изменить сбор',
            onSubmit: async (data) => {
              await updateDuty({ id: duty.id, data });
              mutate();
            },
          });
        }
        if (eventKey === '2') {
          ModalsService.createModal(ConfirmModal, {
            title: 'Удалить сбор',
            buttonText: 'Да',
          }).then(async () => {
            await deleteDuty({ id: duty.id });
          });
        }
      };

      return (
        <Popover ref={ref} className={className} style={{ left, top }} full>
          <Dropdown.Menu onSelect={handleSelect}>
            <Dropdown.Item eventKey={'1'}>
              <i className="bi bi-pencil" />
              <span className="ps-2">Изменить</span>
            </Dropdown.Item>

            <Dropdown.Item eventKey={'2'}>
              <i className="bi bi-trash" />
              <span className="ps-2">Удалить</span>
            </Dropdown.Item>
          </Dropdown.Menu>
        </Popover>
      );
    };

  const ActionCell = ({
    rowData,
    dataKey,
    ...props
  }: CellProps<CleanCustomsDeliveryDuty>) => {
    return (
      canEdit &&
      rowData && (
        <Table.Cell
          {...props}
          className="link-group no-padding-cell pt-2"
          align="right"
        >
          <Rbac object={RbacObject.User} action={'write:admin'}>
            <Whisper
              placement="leftStart"
              trigger="click"
              speaker={renderMenu(rowData)}
            >
              <IconButton
                appearance="subtle"
                icon={<i className="bi bi-three-dots" />}
              />
            </Whisper>
          </Rbac>
        </Table.Cell>
      )
    );
  };

  return (
    <div>
      <div className={'d-flex gap-2 border-bottom border-gray-300 pb-1'}>
        <ul className="nav nav-pills d-inline-flex align-self-start mb-3">
          <li className="nav-item" onClick={() => setType('price')}>
            <NavLink
              to="/customs-deliveries/duties/price"
              className="nav-link text-decoration-none"
            >
              По стоимости
            </NavLink>
          </li>
          <li className="nav-item" onClick={() => setType('code')}>
            {' '}
            <NavLink
              to="/customs-deliveries/duties/code"
              className="nav-link text-decoration-none"
            >
              По коду
            </NavLink>
          </li>
        </ul>
      </div>
      <Table
        loading={isLoading}
        data={data}
        headerHeight={57}
        rowKey="id"
        locale={{
          loading: 'Загрузка',
          emptyMessage: 'Нет сборов',
        }}
        autoHeight
      >
        {' '}
        {type === 'price' && (
          <Table.Column flexGrow={1}>
            <Table.HeaderCell>Условие</Table.HeaderCell>
            <ConditionCell />
          </Table.Column>
        )}
        {type === 'code' && (
          <Table.Column flexGrow={1}>
            <Table.HeaderCell>Код</Table.HeaderCell>
            <CodeCell />
          </Table.Column>
        )}
        <Table.Column flexGrow={1}>
          <Table.HeaderCell>Ставка таможенного сбора</Table.HeaderCell>
          <RateCell />
        </Table.Column>
        <Table.Column flexGrow={0}>
          <Table.HeaderCell children="" />
          <ActionCell />
        </Table.Column>
      </Table>
    </div>
  );
};
